import type { TFunction } from "i18next";
import { FaInfoCircle } from "react-icons/fa";
import { Link } from "react-router-dom";
import { u3125FormatCurrency } from "../../../../../u3125Facture/U3125";
import { NS } from "../../../../U3121";
import type { U3121BillingProjectProfitability } from "../../../../U3121Service";
import U3121BillingSummaryProfitabilityKindBreakdown from "./U3121BillingSummaryProfitabilityKindBreakdown";
import {
  u3121FormatMultiplier,
  u3121FormatPercent,
  u3121ProfitabilityModeMessageKey,
  u3121ProfitabilityProgressBarClass,
  u3121ProfitabilityProgressPercent,
  u3121ProfitabilityStatusBadgeClass,
  u3121ProfitabilityStatusMessageKey,
} from "./u3121BillingSummaryProfitabilityHelpers";

interface U3121BillingSummaryProfitabilitySectionProps {
  profitability: U3121BillingProjectProfitability;
  t: TFunction;
}

interface MetricCardProps {
  label: string;
  value: string;
  tooltip?: string;
}

function ProfitabilityMetricCard({ label, value, tooltip }: MetricCardProps) {
  return (
    <div className="rounded-md border border-gray-200 bg-surface px-3 py-2 text-right">
      <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
        <span>{label}</span>
        {tooltip ? (
          <span title={tooltip} className="cursor-help text-muted-foreground/80">
            <FaInfoCircle className="h-3 w-3" aria-hidden />
          </span>
        ) : null}
      </div>
      <div className="text-sm font-medium tabular-nums">{value}</div>
    </div>
  );
}

function U3121BillingSummaryProfitabilitySection({ profitability, t }: U3121BillingSummaryProfitabilitySectionProps) {
  const { mode, status } = profitability;
  const modeKey = u3121ProfitabilityModeMessageKey(mode);
  const isFixedFee = mode === "FIXED_FEE";
  const isTimeAndMaterial = mode === "TIME_AND_MATERIAL";
  const showKpi = status !== "not_applicable" && status !== "insufficient_data";
  const progress = u3121ProfitabilityProgressPercent(profitability.costConsumedPercent);

  const formatCurrencyOrDash = (value: number | null | undefined) =>
    value != null && Number.isFinite(value) ? u3125FormatCurrency(value) : "—";

  return (
    <section className="space-y-3">
      <div className="rounded-lg border border-gray-200 bg-muted/40 p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground">{t("sectionBillingSummaryProfitability", { ns: NS })}</span>
            <span title={t("tooltipProfitabilityJtdScope", { ns: NS })} className="cursor-help text-muted-foreground">
              <FaInfoCircle className="h-3.5 w-3.5" aria-hidden />
            </span>
            {modeKey ? (
              <span className="inline-flex rounded-full bg-surface px-2.5 py-0.5 text-xs font-medium text-muted-foreground ring-1 ring-gray-200">
                {t(modeKey, { ns: NS })}
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${u3121ProfitabilityStatusBadgeClass(status)}`}
            >
              {t(u3121ProfitabilityStatusMessageKey(status), { ns: NS })}
            </span>
            <Link to="/U3134" className="text-sm text-primary-600 hover:underline">
              {t("btnGoToWip", { ns: NS })}
            </Link>
          </div>
        </div>

        {status === "not_applicable" ? (
          <p className="text-sm text-muted-foreground">{t("msgProfitabilityNotApplicable", { ns: NS })}</p>
        ) : null}

        {status === "insufficient_data" ? (
          <p className="text-sm text-muted-foreground">{t("msgProfitabilityInsufficientData", { ns: NS })}</p>
        ) : null}

        {showKpi && isFixedFee && profitability.costConsumedPercent != null ? (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{t("lblProfitabilityCostConsumedPercent", { ns: NS })}</span>
              <span className="tabular-nums">{u3121FormatPercent(profitability.costConsumedPercent)}</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className={`h-full rounded-full transition-all ${u3121ProfitabilityProgressBarClass(status)}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        ) : null}

        {showKpi ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {isFixedFee ? (
              <ProfitabilityMetricCard
                label={t("lblProfitabilityReferenceRevenue", { ns: NS })}
                value={formatCurrencyOrDash(profitability.referenceRevenue)}
                tooltip={t("tooltipProfitabilityFixedFee", { ns: NS })}
              />
            ) : null}
            {isTimeAndMaterial ? (
              <ProfitabilityMetricCard
                label={t("lblProfitabilityActualRevenue", { ns: NS })}
                value={formatCurrencyOrDash(profitability.actualRevenue)}
                tooltip={t("tooltipProfitabilityTm", { ns: NS })}
              />
            ) : null}
            <ProfitabilityMetricCard
              label={t("lblProfitabilityActualCost", { ns: NS })}
              value={formatCurrencyOrDash(profitability.actualCost)}
              tooltip={t("tooltipProfitabilityJtdScope", { ns: NS })}
            />
            <ProfitabilityMetricCard
              label={isFixedFee ? t("lblProfitabilityProfitAmount", { ns: NS }) : t("lblProfitabilityMarginAmount", { ns: NS })}
              value={formatCurrencyOrDash(profitability.profitOrMarginAmount)}
              tooltip={isFixedFee ? t("tooltipProfitabilityFixedFee", { ns: NS }) : t("tooltipProfitabilityTm", { ns: NS })}
            />
            <ProfitabilityMetricCard
              label={isFixedFee ? t("lblProfitabilityProfitPercent", { ns: NS }) : t("lblProfitabilityMarginPercent", { ns: NS })}
              value={u3121FormatPercent(profitability.profitOrMarginPercent)}
            />
            {isFixedFee ? (
              <ProfitabilityMetricCard
                label={t("lblProfitabilityCostConsumedPercent", { ns: NS })}
                value={u3121FormatPercent(profitability.costConsumedPercent)}
              />
            ) : null}
            {isTimeAndMaterial ? (
              <ProfitabilityMetricCard
                label={t("lblProfitabilityMultiplier", { ns: NS })}
                value={u3121FormatMultiplier(profitability.multiplier)}
                tooltip={t("tooltipProfitabilityTm", { ns: NS })}
              />
            ) : null}
          </div>
        ) : null}

        {showKpi ? <U3121BillingSummaryProfitabilityKindBreakdown byKind={profitability.byKind} t={t} /> : null}

        {showKpi ? (
          <p className="text-xs text-muted-foreground">{t("msgProfitabilityJtdVsInvoicedDisclaimer", { ns: NS })}</p>
        ) : null}
      </div>
    </section>
  );
}

export default U3121BillingSummaryProfitabilitySection;
